import { c as _c } from "react/compiler-runtime";
import { Facet, SelectedFacet } from '@spa/components/facets/interfaces';
import { Fragment, ReactElement, ReactNode, memo, useEffect, useMemo, useRef } from 'react';
import { ListAttribution, WishlistsActionOrigin, WishlistsActionsOriginType } from '@spa/lib/tracking/trackWishlistsActions';
import { SearchTrackingResponse, useSearchResultTracking } from '@spa/lib/tracking/useSearchResultTracking';
import trackFiltersActions, { FiltersEventType, generateFacetData } from '@spa/lib/tracking/trackFiltersActions';
import { CarouselJsonLd } from 'next-seo';
import ConfigurableCmsComponentMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-component-metadata.component';
import ConfigurableCmsSlotMetadata from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-slot-metadata.component';
import { CountryCodes } from '@spa/lib/const';
import type { ItemListEventItem } from '@spa/lib/tracking/eCommerceTrackingEventSchema';
import { Metadata } from '@spa/pages-converters/cms/component-envelops/CmsComponentEnvelopBase';
import { MutableRefObject } from 'preact/compat';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { PageTemplate } from '@spa/lib/cms/const';
import { ProductActionsOriginType } from '@spa/lib/tracking/trackProductActions';
import ProductTileComponent from '@spa/components/product-list/product-tile/product-tile.component';
import { Product as ProductTileComponentProduct } from '@spa/components/product-list/product-tile/product-tile.component';
import { ProductTileContext } from './product-tile/product-tile.context';
import { ProductTileContextValue } from './product-tile/product-tile.context';
import SelectedFacetsComponent from '@spa/components/selected-facets/selected-facets.component';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import buildStructuredProductData from '@spa/lib/seo/buildStructuredProductData';
import { convertProductToCommonECommerceEventItem } from '@spa/lib/tracking/convertProductToCommonECommerceEventItem';
import getAuthSessionUserId from '@spa/lib/auth/getAuthSessionUserId';
import selectCountryCode from '@spa/lib/store/settings/selectors/selectCountryCode';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import selectLastQueryMetadata from '@spa/lib/store/search/selectors/selectLastQueryMetadata';
import selectLocale from '@spa/lib/store/settings/selectors/selectLocale';
import selectPageTemplate from '@spa/lib/store/router/selectors/selectPageTemplate';
import selectPricesIncludeTax from '@spa/lib/store/settings/selectors/selectPricesIncludeTax';
import selectPublicBaseUrl from '@spa/lib/store/settings/selectors/selectPublicBaseUrl';
import selectSearchSettings from '@spa/lib/store/settings/selectors/selectSearchSettings';
import selectWishlistSettings from '@spa/lib/store/settings/selectors/selectWishlistSettings';
import styles from '@spa/components/product-list/product-list.component.module.scss';
import useFullPath from '@spa/lib/react/hooks/useFullPath';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
import { usePathname } from 'next/navigation';
import { usePopularityEventsAPI } from '@spa/lib/react/hooks/usePopularityEventsAPI';
import { useProductListTracking } from '@spa/components/product-list/useProductListTracking';
import { useSelector } from 'react-redux';
type CmsComponentEnvelop = import('@spa/pages-converters/cms/component-envelops/CmsComponentEnvelop').CmsComponentEnvelop;
export type Product = ProductTileComponentProduct;
export interface BaseProductTeaserContainer<C> {
  readonly displayPosition: number;
  readonly childrenMetadata: ReadonlyArray<Metadata>;
  readonly children: C;
  readonly metadata: ReadonlyArray<Metadata>;
}
export type ProductTeaserContainer = BaseProductTeaserContainer<ReadonlyArray<CmsComponentEnvelop>>;
export type ProductTeaserContainerWithNodes = BaseProductTeaserContainer<ReactNode>;
export interface BaseProps<PT> {
  readonly componentId: string;
  readonly editorMode: boolean;
  readonly facets: ReadonlyArray<Facet>;
  readonly listId: string;
  readonly noResultsHtml: string;
  readonly priority: boolean | null;
  readonly products: ReadonlyArray<Product>;
  readonly productTeaser: ReadonlyArray<PT>;
  readonly productTeaserMetadata: ReadonlyArray<Metadata>;
  readonly searchTracking?: SearchTrackingResponse;
  readonly selectedFacets: ReadonlyArray<SelectedFacet>;
  readonly title: string;
}
export type DataProps = BaseProps<ProductTeaserContainer>;
export type Props = BaseProps<ProductTeaserContainerWithNodes>;
interface TrackingData {
  readonly productTrackingActionData: ProductActionsOriginType;
  readonly wishlistListAttribution: ListAttribution;
  readonly wishlistTrackingActionData: WishlistsActionsOriginType;
}
const createTileComponent = (product: ProductTileComponentProduct, index: number, searchFilters: ReadonlyArray<string>, {
  productTrackingActionData,
  wishlistListAttribution,
  wishlistTrackingActionData
}: TrackingData, priority: boolean | null): React.ReactElement => {
  return <div key={product.code} className={styles['product-list__tile']} data-product-index={index}>
      <ProductTileComponent product={product} priority={priority === true && (index === 0 || index === 1)} productActionOrigin={productTrackingActionData} wishlistActionOrigin={wishlistTrackingActionData} wishlistListAttribution={wishlistListAttribution} productIndex={index} searchFilters={searchFilters} />
    </div>;
};
function ProductListComponent(t0) {
  const $ = _c(112);
  const {
    editorMode,
    facets,
    listId,
    noResultsHtml,
    priority,
    products,
    productTeaser,
    productTeaserMetadata,
    searchTracking,
    selectedFacets,
    title
  } = t0;
  const pathname = usePathname();
  const productListRef = useRef(null);
  const publicBaseUrl = useSelector(selectPublicBaseUrl);
  const hasTrackedFilters = useRef(false);
  const {
    preOrderEnabled
  } = useSelector(selectGeneralSettings);
  const countryCode = useSelector(selectCountryCode);
  const shouldHidePrices = countryCode === CountryCodes.HU;
  const lastQueryMetadata = useSelector(selectLastQueryMetadata);
  const locale = useSelector(selectLocale);
  const pricesIncludeTax = useSelector(selectPricesIncludeTax);
  const {
    popularityEventsEnabled
  } = useSelector(selectSearchSettings);
  const {
    plpEnabled: isWishlistEnabled
  } = useSelector(selectWishlistSettings);
  const pageTemplate = useSelector(selectPageTemplate);
  const {
    fullPath
  } = useFullPath();
  const namespacedIntl = useNamespacedIntl("product-tile");
  const {
    authSession,
    session
  } = useNextAuthOrSession();
  const {
    sendClickEvent
  } = usePopularityEventsAPI();
  const anonymousUserId = session.guid;
  let t1;
  if ($[0] !== authSession) {
    t1 = getAuthSessionUserId(authSession);
    $[0] = authSession;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const userId = t1;
  let t2;
  if ($[2] !== listId || $[3] !== products || $[4] !== searchTracking) {
    t2 = {
      listId,
      products,
      response: searchTracking
    };
    $[2] = listId;
    $[3] = products;
    $[4] = searchTracking;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useSearchResultTracking(t2);
  let t3;
  if ($[6] !== countryCode || $[7] !== listId || $[8] !== preOrderEnabled || $[9] !== pricesIncludeTax || $[10] !== products || $[11] !== title) {
    let t4;
    if ($[13] !== countryCode || $[14] !== listId || $[15] !== preOrderEnabled || $[16] !== pricesIncludeTax || $[17] !== title) {
      t4 = (product, index) => {
        const eCommerceEventItem = convertProductToCommonECommerceEventItem({
          product,
          listContext: {
            originId: listId,
            originType: title
          },
          country: countryCode,
          preOrderEnabled,
          pricesIncludeTax
        });
        return {
          ...eCommerceEventItem,
          item_list_id: listId,
          item_list_name: title,
          index
        };
      };
      $[13] = countryCode;
      $[14] = listId;
      $[15] = preOrderEnabled;
      $[16] = pricesIncludeTax;
      $[17] = title;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    t3 = products.map(t4);
    $[6] = countryCode;
    $[7] = listId;
    $[8] = preOrderEnabled;
    $[9] = pricesIncludeTax;
    $[10] = products;
    $[11] = title;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const viewItemListEventItems = t3;
  let t4;
  if ($[19] !== anonymousUserId || $[20] !== listId || $[21] !== products.length || $[22] !== title || $[23] !== userId || $[24] !== viewItemListEventItems) {
    t4 = {
      anonymousId: anonymousUserId,
      authenticatedId: userId,
      listId,
      listName: title,
      productListRef,
      productsCount: products.length,
      viewItemListEventItems
    };
    $[19] = anonymousUserId;
    $[20] = listId;
    $[21] = products.length;
    $[22] = title;
    $[23] = userId;
    $[24] = viewItemListEventItems;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  useProductListTracking(t4);
  const countTotalNumberOfItems = _temp;
  let t5;
  if ($[26] !== listId || $[27] !== title) {
    t5 = {
      originId: listId,
      originType: title
    };
    $[26] = listId;
    $[27] = title;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  const listOrigin = t5;
  let t6;
  if ($[29] !== listOrigin.originId || $[30] !== listOrigin.originType) {
    t6 = {
      itemListId: listOrigin.originId,
      itemListName: listOrigin.originType
    };
    $[29] = listOrigin.originId;
    $[30] = listOrigin.originType;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== listId) {
    t7 = {
      originId: listId,
      originType: WishlistsActionOrigin.PLP
    };
    $[32] = listId;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== listOrigin || $[35] !== t6 || $[36] !== t7) {
    t8 = {
      productTrackingActionData: listOrigin,
      wishlistListAttribution: t6,
      wishlistTrackingActionData: t7
    };
    $[34] = listOrigin;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  const trackingActionData = t8;
  let t9;
  if ($[38] !== selectedFacets) {
    t9 = generateFacetData(selectedFacets);
    $[38] = selectedFacets;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  const searchFilters = t9;
  let t10;
  if ($[40] !== pathname) {
    const categoryPath = pathname.split("/");
    t10 = categoryPath[categoryPath.length - 2].split("-").pop();
    $[40] = pathname;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const categoryCode = t10;
  let t11;
  if ($[42] !== anonymousUserId || $[43] !== categoryCode || $[44] !== lastQueryMetadata || $[45] !== searchFilters || $[46] !== userId) {
    t11 = () => {
      if (!hasTrackedFilters.current) {
        trackFiltersActions(FiltersEventType.VIEWED, searchFilters, anonymousUserId, userId, lastQueryMetadata, categoryCode);
        hasTrackedFilters.current = true;
      }
    };
    $[42] = anonymousUserId;
    $[43] = categoryCode;
    $[44] = lastQueryMetadata;
    $[45] = searchFilters;
    $[46] = userId;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== anonymousUserId || $[49] !== categoryCode || $[50] !== facets || $[51] !== lastQueryMetadata || $[52] !== searchFilters || $[53] !== selectedFacets || $[54] !== userId) {
    t12 = [facets, lastQueryMetadata, anonymousUserId, userId, selectedFacets, categoryCode, searchFilters];
    $[48] = anonymousUserId;
    $[49] = categoryCode;
    $[50] = facets;
    $[51] = lastQueryMetadata;
    $[52] = searchFilters;
    $[53] = selectedFacets;
    $[54] = userId;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  useEffect(t11, t12);
  let t13;
  if ($[56] !== selectedFacets) {
    t13 = <div className={styles["product-list__facets-wrapper"]}><SelectedFacetsComponent selectedFacets={selectedFacets} /></div>;
    $[56] = selectedFacets;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  const selectedFacetsComponent = t13;
  let nodes;
  if ($[58] !== editorMode || $[59] !== priority || $[60] !== productTeaser || $[61] !== products || $[62] !== searchFilters || $[63] !== trackingActionData) {
    nodes = [];
    let currentIndex = 0;
    for (let index_0 = 0; index_0 < productTeaser.length; index_0++) {
      const teaser = productTeaser[index_0];
      if (!editorMode && (!teaser.children || Array.isArray(teaser.children) && teaser.children.length === 0)) {
        continue;
      }
      const nextIndex = currentIndex + teaser.displayPosition;
      const slice = products.slice(currentIndex, nextIndex);
      if (slice.length === teaser.displayPosition) {
        const tiles = slice.map((product_0, idx) => createTileComponent(product_0, currentIndex + idx, searchFilters, trackingActionData, priority));
        nodes.push(<Fragment key={`${slice[0].code}-${teaser.displayPosition}`}>{tiles}<div className={styles["product-list__teaser-container"]}>{editorMode && <ConfigurableCmsComponentMetadata beforeComponent={true} metadata={teaser.metadata} />}{editorMode && <ConfigurableCmsSlotMetadata beforeSlot={true} metadata={teaser.childrenMetadata} />}{teaser.children}{editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={teaser.childrenMetadata} />}{editorMode && <ConfigurableCmsComponentMetadata beforeComponent={false} metadata={teaser.metadata} />}</div></Fragment>);
        currentIndex = nextIndex;
      }
    }
    if (currentIndex < products.length) {
      const tail = products.slice(currentIndex).map((product_1, idx_0) => createTileComponent(product_1, currentIndex + idx_0, searchFilters, trackingActionData, priority));
      nodes.push(...tail);
    }
    $[58] = editorMode;
    $[59] = priority;
    $[60] = productTeaser;
    $[61] = products;
    $[62] = searchFilters;
    $[63] = trackingActionData;
    $[64] = nodes;
  } else {
    nodes = $[64];
  }
  const teaserNodes = nodes;
  let t14;
  if ($[65] !== authSession || $[66] !== session) {
    t14 = {
      authSession,
      session
    };
    $[65] = authSession;
    $[66] = session;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  let t15;
  if ($[68] !== countryCode || $[69] !== fullPath || $[70] !== isWishlistEnabled || $[71] !== lastQueryMetadata || $[72] !== locale || $[73] !== namespacedIntl || $[74] !== pageTemplate || $[75] !== pathname || $[76] !== popularityEventsEnabled || $[77] !== preOrderEnabled || $[78] !== pricesIncludeTax || $[79] !== sendClickEvent || $[80] !== t14) {
    t15 = {
      countryCode,
      fullPath,
      isWishlistEnabled,
      lastQueryMetadata,
      locale,
      namespacedIntl,
      pageTemplate,
      pathname,
      popularityEventsEnabled,
      preOrderEnabled,
      pricesIncludeTax,
      sendClickEvent,
      sessionsData: t14
    };
    $[68] = countryCode;
    $[69] = fullPath;
    $[70] = isWishlistEnabled;
    $[71] = lastQueryMetadata;
    $[72] = locale;
    $[73] = namespacedIntl;
    $[74] = pageTemplate;
    $[75] = pathname;
    $[76] = popularityEventsEnabled;
    $[77] = preOrderEnabled;
    $[78] = pricesIncludeTax;
    $[79] = sendClickEvent;
    $[80] = t14;
    $[81] = t15;
  } else {
    t15 = $[81];
  }
  const productTileContextValue = t15;
  let t16;
  if ($[82] !== lastQueryMetadata?.totalCount || $[83] !== pathname || $[84] !== pricesIncludeTax || $[85] !== products || $[86] !== publicBaseUrl || $[87] !== shouldHidePrices) {
    t16 = products.length > 0 && <CarouselJsonLd useAppDir={true} ofType="product" url={`${publicBaseUrl}${pathname}`} numberOfItems={countTotalNumberOfItems(lastQueryMetadata?.totalCount ?? "", products.length)} data={buildStructuredProductData(products, publicBaseUrl, pricesIncludeTax, shouldHidePrices)} />;
    $[82] = lastQueryMetadata?.totalCount;
    $[83] = pathname;
    $[84] = pricesIncludeTax;
    $[85] = products;
    $[86] = publicBaseUrl;
    $[87] = shouldHidePrices;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  let t17;
  if ($[89] !== editorMode || $[90] !== productTeaserMetadata) {
    t17 = editorMode && <ConfigurableCmsSlotMetadata beforeSlot={true} metadata={productTeaserMetadata} />;
    $[89] = editorMode;
    $[90] = productTeaserMetadata;
    $[91] = t17;
  } else {
    t17 = $[91];
  }
  const t18 = selectedFacets.length > 0 && selectedFacetsComponent;
  let t19;
  if ($[92] !== noResultsHtml || $[93] !== products.length || $[94] !== teaserNodes) {
    t19 = products.length > 0 ? <div className={styles["product-list__tiles-wrapper"]}>{teaserNodes}</div> : <div dangerouslySetInnerHTML={{
      __html: noResultsHtml
    }} />;
    $[92] = noResultsHtml;
    $[93] = products.length;
    $[94] = teaserNodes;
    $[95] = t19;
  } else {
    t19 = $[95];
  }
  let t20;
  if ($[96] !== t18 || $[97] !== t19) {
    t20 = <div className={styles["product-list"]} ref={productListRef}>{t18}{t19}</div>;
    $[96] = t18;
    $[97] = t19;
    $[98] = t20;
  } else {
    t20 = $[98];
  }
  let t21;
  if ($[99] !== editorMode || $[100] !== productTeaserMetadata) {
    t21 = editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={productTeaserMetadata} />;
    $[99] = editorMode;
    $[100] = productTeaserMetadata;
    $[101] = t21;
  } else {
    t21 = $[101];
  }
  let t22;
  if ($[102] !== t17 || $[103] !== t20 || $[104] !== t21) {
    t22 = <div>{t17}{t20}{t21}</div>;
    $[102] = t17;
    $[103] = t20;
    $[104] = t21;
    $[105] = t22;
  } else {
    t22 = $[105];
  }
  let t23;
  if ($[106] !== t16 || $[107] !== t22) {
    t23 = <>{t16}{t22}</>;
    $[106] = t16;
    $[107] = t22;
    $[108] = t23;
  } else {
    t23 = $[108];
  }
  let t24;
  if ($[109] !== productTileContextValue || $[110] !== t23) {
    t24 = <ProductTileContext.Provider value={productTileContextValue}>{t23}</ProductTileContext.Provider>;
    $[109] = productTileContextValue;
    $[110] = t23;
    $[111] = t24;
  } else {
    t24 = $[111];
  }
  return t24;
}
function _temp(totalCount, productsLength) {
  const total = parseInt(totalCount, 10);
  return Number.isFinite(total) ? total : productsLength;
}
export default memo(ProductListComponent);